import { useRef, type ReactNode } from "react"
import cx from "clsx"
import { AdminButton } from "@rawa/ui/AdminButton"
import { ErrorCallout } from "@rawa/ui/ErrorCallout"
import { AdminDialog } from "./AdminDialog"
import typography from "@rawa/ui/styles/typography.module.css"
import styles from "./ConfirmDialog.module.css"

/**
 * Asks before something that cannot be undone. Opens on Cancel, so Enter does
 * not go ahead by accident; Escape and the corner close both cancel. A caller
 * that keeps it open while going ahead says so with `pending`, and why it
 * failed with `error`.
 */
export function ConfirmDialog({
  open,
  title,
  confirmLabel,
  pending = false,
  error,
  onConfirm,
  onCancel,
  children,
}: {
  open: boolean
  title: string
  confirmLabel: string
  pending?: boolean
  error?: ReactNode
  onConfirm: () => void
  onCancel: () => void
  children: ReactNode
}) {
  const cancel = useRef<HTMLButtonElement>(null)

  return (
    <AdminDialog
      open={open}
      onClose={onCancel}
      labelledBy="confirm-title"
      initialFocus={cancel}
      className={styles.dialog}
    >
      <h2 id="confirm-title" className={cx(typography.h5, styles.title)}>
        {title}
      </h2>
      <p className={styles.body}>{children}</p>
      {error && <ErrorCallout>{error}</ErrorCallout>}
      <div className={styles.actions}>
        <AdminButton variant="outline" ref={cancel} onClick={onCancel}>
          Cancel
        </AdminButton>
        <AdminButton
          onClick={() => !pending && onConfirm()}
          ariaDisabled={pending}
        >
          {confirmLabel}
        </AdminButton>
      </div>
    </AdminDialog>
  )
}
